import React, { type ComponentRef } from 'react'

import * as HoverCard from '@radix-ui/react-hover-card'
import { cva } from 'class-variance-authority'

import { Icons } from '@/components/atoms/Icons'
import { cn } from '@/lib/utils.ts'

import { type MobileRequestProps } from '../MobileRequest'

const messageTitleVariants = cva('text-lg font-bold', {
  variants: {
    state: {
      open: 'text-green-500',
      exceedsThreshold: 'text-orange-400',
      pause: 'text-red-800',
      adminPause: 'text-red-800',
    },
  },
})

const iconVariants = cva('w-6', {
  variants: {
    state: {
      open: 'fill-green-500',
      exceedsThreshold: 'fill-orange-400',
      pause: 'fill-red-800',
      adminPause: 'fill-red-800',
    },
  },
})

export type MobileRequestStatusPopupData = Pick<MobileRequestProps, 'state'>

export interface MobileRequestStatusPopupLabels {
  openMessageTitle: string
  openMessageText: string
  exceedsThresholdMessageTitle: string
  exceedsThresholdMessageText: string
  pauseMessageTitle: string
  pauseMessageText: string
  adminPauseMessageTitle: string
  adminPauseMessageText: string
}

export interface MobileRequestStatusPopupProps
  extends React.HtmlHTMLAttributes<HTMLDivElement>, MobileRequestStatusPopupData {
  labels: MobileRequestStatusPopupLabels
}

export const MobileRequestStatusPopup = React.forwardRef<
  ComponentRef<typeof HoverCard.Trigger>,
  MobileRequestStatusPopupProps
>(({ className, state, labels, children, ...props }, ref) => (
  <HoverCard.Root openDelay={0} closeDelay={100} {...props}>
    <HoverCard.Trigger ref={ref}>{children}</HoverCard.Trigger>
    <HoverCard.Portal>
      <HoverCard.Content align="start">
        <div
          className={cn(
            'mt-1 flex w-[312px] flex-col gap-9 rounded-[8px] border border-black bg-white px-4 py-5 shadow-xl',
            className
          )}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              {state === 'open' && <Icons.checkCircle className={cn(iconVariants({ state }))} />}
              {state === 'exceedsThreshold' && <Icons.pauseCircle className={cn(iconVariants({ state }))} />}
              {(state === 'pause' || state === 'adminPause') && (
                <Icons.mobileOff className={cn(iconVariants({ state }))} />
              )}
              <span className={cn(messageTitleVariants({ state }))}>
                {state === 'open'
                  ? labels.openMessageTitle
                  : state === 'exceedsThreshold'
                    ? labels.exceedsThresholdMessageTitle
                    : state === 'pause'
                      ? labels.pauseMessageTitle
                      : labels.adminPauseMessageTitle}
              </span>
            </div>
            <p className="text-base whitespace-pre-wrap text-black">
              {state === 'open'
                ? labels.openMessageText
                : state === 'exceedsThreshold'
                  ? labels.exceedsThresholdMessageText
                  : state === 'pause'
                    ? labels.pauseMessageText
                    : labels.adminPauseMessageText}
            </p>
          </div>
        </div>
      </HoverCard.Content>
    </HoverCard.Portal>
  </HoverCard.Root>
))
